import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TwoFactorCard from "@/components/TwoFactorCard";
import { Save, Mail, MailCheck, MailX, Sparkles, Settings as SettingsIcon, Code2 } from "lucide-react";
import { toast } from "sonner";

const PREVIEW_CTX = {
  tenant_name: "Jane Tenant",
  landlord_name: "Your Name",
  property_name: "Maple Court · Apt 2B",
  property_address: "12 Maple Court, Brooklyn NY",
  period: "2026-02",
  amount: "1,500",
  days_overdue: 7,
  grace_day: 5,
};

function renderTpl(tpl, ctx) {
  if (!tpl) return "";
  return tpl.replace(/\{(\w+)\}/g, (m, k) => (k in ctx ? String(ctx[k]) : m));
}

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "every_3_days", label: "Every 3 days" },
  { value: "weekly", label: "Weekly (every 7 days)" },
];

const PLACEHOLDERS = ["tenant_name", "landlord_name", "property_name", "property_address", "period", "amount", "days_overdue", "grace_day"];

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState(null);

  useEffect(() => {
    api.get("/settings").then(r => setS(r.data));
  }, []);

  if (!s) return <AppLayout title="Settings" subtitle="Loading…"><div className="text-slate-400 text-sm">Loading…</div></AppLayout>;

  const onField = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        grace_day: Number(s.grace_day),
        auto_reminder_enabled: !!s.auto_reminder_enabled,
        auto_reminder_frequency: s.auto_reminder_frequency,
        sender_email: s.sender_email,
        subject_template: s.subject_template,
        body_template: s.body_template,
      };
      const { data } = await api.put("/settings", payload);
      setS({ ...data, available_placeholders: s.available_placeholders });
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/test-reminder");
      setLastTest(data);
      if (data.email_status === "sent") toast.success("Test email sent to your address");
      else if (data.email_status === "failed") setLastTest(data); // inline card shows the error
      else toast.info("Email not configured — preview only");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setTesting(false);
  };

  const insertPlaceholder = (field, ph) => {
    setS(prev => ({ ...prev, [field]: (prev[field] || "") + `{${ph}}` }));
  };

  return (
    <AppLayout
      title="Settings"
      subtitle="Configure your collections workflow"
      actions={
        <div className="flex gap-2">
          <Button data-testid="test-email-button" onClick={testEmail} disabled={testing} variant="outline" className="gap-2">
            <Mail className="h-4 w-4" /> {testing ? "Sending…" : "Send Test Email"}
          </Button>
          <Button data-testid="save-settings-button" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: form */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-slate-200" data-testid="overdue-settings-card">
            <div className="flex items-center gap-2 mb-1">
              <SettingsIcon className="h-4 w-4 text-slate-500" />
              <h3 className="font-display font-semibold">Overdue policy</h3>
            </div>
            <p className="text-xs text-slate-500 mb-5">When is a rent considered late?</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Grace day of the month</Label>
                <Input
                  data-testid="grace-day-input"
                  type="number" min={1} max={28}
                  value={s.grace_day}
                  onChange={e => onField("grace_day", e.target.value)}
                />
                <p className="text-xs text-slate-500">Invoices unpaid past day <span className="font-mono">{s.grace_day || 5}</span> become overdue.</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-slate-200" data-testid="auto-reminder-card">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-slate-500" />
                <h3 className="font-display font-semibold">Auto-reminder schedule</h3>
              </div>
              <Switch
                data-testid="auto-reminder-toggle"
                checked={!!s.auto_reminder_enabled}
                onCheckedChange={(v) => onField("auto_reminder_enabled", v)}
              />
            </div>
            <p className="text-xs text-slate-500 mb-5">When on, RojasProperty sends reminders automatically every day at 09:00 UTC for any overdue invoice that hasn&apos;t been reminded within the chosen interval.</p>
            <div className={`space-y-1.5 transition-opacity duration-200 ${s.auto_reminder_enabled ? "opacity-100" : "opacity-50"}`}>
              <Label className="text-xs">Reminder frequency</Label>
              <Select
                value={s.auto_reminder_frequency}
                onValueChange={(v) => onField("auto_reminder_frequency", v)}
                disabled={!s.auto_reminder_enabled}
              >
                <SelectTrigger data-testid="frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </Card>

          <Card className="p-6 border-slate-200" data-testid="email-card">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="h-4 w-4 text-slate-500" />
              <h3 className="font-display font-semibold">Email delivery</h3>
            </div>
            <p className="text-xs text-slate-500 mb-5">The address reminders are sent from. Use the Resend sandbox for testing, or your own verified domain.</p>
            <div className="space-y-1.5">
              <Label className="text-xs">From email address</Label>
              <Input
                data-testid="sender-email-input"
                type="email"
                value={s.sender_email}
                onChange={e => onField("sender_email", e.target.value)}
                placeholder="rent@yourdomain.com"
              />
              <p className="text-xs text-slate-500">Verify a domain at <span className="font-mono">resend.com/domains</span> to send to any address.</p>
            </div>
          </Card>

          <Card className="p-6 border-slate-200" data-testid="template-card">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Code2 className="h-4 w-4 text-slate-500" />
                <h3 className="font-display font-semibold">Message template</h3>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-4">Use placeholders like <span className="font-mono">{"{tenant_name}"}</span> — they&apos;ll be filled in for each tenant.</p>

            <div className="flex flex-wrap gap-1.5 mb-4">
              <span className="text-xs text-slate-500 mr-1 leading-7">Insert:</span>
              {PLACEHOLDERS.map(ph => (
                <button
                  key={ph}
                  type="button"
                  data-testid={`insert-${ph}`}
                  onClick={() => insertPlaceholder("body_template", ph)}
                  className="text-xs px-2 py-0.5 rounded-md bg-slate-100 hover:bg-slate-200 transition-colors font-mono text-slate-700"
                >{`{${ph}}`}</button>
              ))}
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Subject line</Label>
                <Input
                  data-testid="subject-template-input"
                  value={s.subject_template}
                  onChange={e => onField("subject_template", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Body</Label>
                <Textarea
                  data-testid="body-template-input"
                  rows={10}
                  className="font-mono text-sm"
                  value={s.body_template}
                  onChange={e => onField("body_template", e.target.value)}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* RIGHT: live preview */}
        <div className="space-y-6">
          <Card className="p-6 border-slate-200 sticky top-6" data-testid="preview-card">
            <div className="flex items-center gap-2 mb-1">
              <MailCheck className="h-4 w-4 text-emerald-600" />
              <h3 className="font-display font-semibold">Live preview</h3>
            </div>
            <p className="text-xs text-slate-500 mb-4">How the next reminder will look (sample data).</p>

            <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
              <div className="px-4 py-2.5 border-b border-slate-200 bg-white text-xs text-slate-500 space-y-0.5">
                <div><span className="text-slate-400">From:</span> <span className="font-mono">{s.sender_email}</span></div>
                <div><span className="text-slate-400">To:</span> <span className="font-mono">jane@example.com</span></div>
                <div><span className="text-slate-400">Subject:</span> <span className="font-medium text-slate-900" data-testid="preview-subject">{renderTpl(s.subject_template, PREVIEW_CTX)}</span></div>
              </div>
              <div className="p-4 bg-white">
                <pre className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed font-sans" data-testid="preview-body">{renderTpl(s.body_template, PREVIEW_CTX)}</pre>
                <div className="mt-4 p-3 bg-slate-100 rounded-md">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Amount due</div>
                  <div className="font-mono-num text-xl font-semibold">${PREVIEW_CTX.amount}</div>
                </div>
              </div>
            </div>
            {lastTest && (
              <div className={`mt-4 p-3 rounded-md text-xs ${lastTest.email_status === "sent" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`}>
                {lastTest.email_status === "sent" ? (
                  <span className="flex items-center gap-1.5"><MailCheck className="h-3.5 w-3.5" /> Test sent · id: <span className="font-mono">{lastTest.email_id?.slice(0, 12)}…</span></span>
                ) : (
                  <span className="flex items-start gap-1.5"><MailX className="h-3.5 w-3.5 mt-0.5" /><span>{lastTest.error || lastTest.reason}</span></span>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* 2FA card */}
        <div className="lg:col-span-3 mt-2">
          <TwoFactorCard />
        </div>
      </div>
    </AppLayout>
  );
}
